// src/components/tires/EditTire.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import { buildAssetUrl } from '../../utils/helpers';
import {
    Save,
    AlertCircle,
    CheckCircle,
    Package,
    DollarSign,
    Info,
    ChevronRight,
    Plus,
    Trash2,
    MapPin,
} from 'lucide-react';
import {
    tiresAPI,
    tireBrandsAPI,
    tireTypesAPI,
    patternsAPI,
    suppliersAPI,
    locationsAPI,
    madeInsAPI,
    zonesAPI
} from '../../services/garageApi';

const EditTire = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [dataLoaded, setDataLoaded] = useState(false);
    const [existingImage, setExistingImage] = useState(null);
    const [newImage, setNewImage] = useState(null);

    // Dropdown data
    const [tireBrands, setTireBrands] = useState([]);
    const [, setTireTypes] = useState([]);
    const [allTireTypes, setAllTireTypes] = useState([]);
    const [filteredTireTypes, setFilteredTireTypes] = useState([]);
    const [patterns, setPatterns] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [madeIns, setMadeIns] = useState([]);
    const [dateOfProduction, setDateOfProduction] = useState('');
    const [dotWeekInput, setDotWeekInput] = useState('');

    // Distribution state
    const [totalQuantity, setTotalQuantity] = useState('');
    const [distributions, setDistributions] = useState([
        { location_id: '', zone_id: '', quantity: '' }
    ]);
    const [zonesCache, setZonesCache] = useState({});
    const [inventoryChanged, setInventoryChanged] = useState(false);

    const calculateDateFromWeek = (year, week) => {
        const jan4 = new Date(year, 0, 4);
        const dayOfWeek = jan4.getDay();
        const thursdayOffset = dayOfWeek <= 4 ? 4 - dayOfWeek : 4 + 7 - dayOfWeek;
        const week1Thursday = new Date(jan4);
        week1Thursday.setDate(jan4.getDate() + thursdayOffset);
        const targetThursday = new Date(week1Thursday);
        targetThursday.setDate(week1Thursday.getDate() + (week - 1) * 7);
        const monday = new Date(targetThursday);
        monday.setDate(targetThursday.getDate() - 3);
        const yearStr = monday.getFullYear();
        const monthStr = String(monday.getMonth() + 1).padStart(2, '0');
        const dayStr = String(monday.getDate()).padStart(2, '0');
        return `${yearStr}-${monthStr}-${dayStr}`;
    };

    // Form data
    const [formData, setFormData] = useState({
        // Step 0: Basic
        tire_brand_id: '',
        pattern_id: '',
        tire_type_id: '',
        size: '',
        dot: '',
        construction_type: 'radial',
        // Step 1: Specifications
        supplier_id: '',
        made_in_id: '',
        date_received: '',
        active: true,
        run_flat: false,
        fuel_rating: '',
        water_rating: '',
        notes: '',
        image: null,
        // Step 2: Pricing
        cost_price: '',
        selling_price: '',
        tax_rate: '',
        is_tax_inclusive: false,
    });

    const steps = [
        { id: 0, name: 'Basic Information', icon: Package },
        { id: 1, name: 'Specifications', icon: Info },
        { id: 2, name: 'Pricing & Inventory', icon: DollarSign },
        { id: 3, name: 'Review & Submit', icon: CheckCircle }
    ];

    // Stable preview URL for the selected image File; revoked when the File changes/unmounts
    const previewFile = (newImage && newImage instanceof File)
        ? newImage
        : (formData.image instanceof File ? formData.image : null);
    const previewUrl = useMemo(
        () => (previewFile ? URL.createObjectURL(previewFile) : null),
        [previewFile]
    );
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    // Helper function to normalize IDs for comparison
    const normalizeId = (id) => {
        if (id === null || id === undefined || id === '') return '';
        return String(id);
    };

    // Get ID from field (handles objects and direct IDs)
    const getIdFromField = (field) => {
        if (!field) return '';
        if (typeof field === 'string' || typeof field === 'number') return normalizeId(field);
        if (typeof field === 'object' && field.id !== undefined) return normalizeId(field.id);
        return '';
    };

    // Extract data from API responses
    const extractData = (response) => {
        if (!response) return [];
        if (Array.isArray(response.data)) return response.data;
        if (response.data && typeof response.data === 'object') {
            if (response.data.data) {
                if (Array.isArray(response.data.data)) return response.data.data;
                if (response.data.data.data && Array.isArray(response.data.data.data)) return response.data.data.data;
            }
            if (response.data.success !== undefined && Array.isArray(response.data.data)) return response.data.data;
        }
        return [];
    };

    // Fetch dropdown data
    const fetchDropdownData = useCallback(async () => {
        try {
            const [brandsRes, typesRes, suppliersRes, locationsRes, madeInsRes] = await Promise.all([
                tireBrandsAPI.getAll(),
                tireTypesAPI.getAll(),
                suppliersAPI.getAll(),
                locationsAPI.getAll(),
                madeInsAPI.getAll()
            ]);

            const typesData = extractData(typesRes);
            setTireBrands(extractData(brandsRes));
            setTireTypes(typesData);
            setAllTireTypes(typesData);
            setFilteredTireTypes(typesData);
            setSuppliers(extractData(suppliersRes));
            setLocations(extractData(locationsRes));
            setMadeIns(extractData(madeInsRes));
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
            setErrors({ submit: 'Failed to load form options. Please refresh the page.' });
        }
    }, []);

    // Fetch tire data
    const fetchTireData = useCallback(async () => {
        try {
            const response = await tiresAPI.show(id);

            let tireData = null;
            if (response.data) {
                if (response.data.success !== undefined && response.data.data) {
                    tireData = response.data.data;
                } else if (response.data.data) {
                    tireData = response.data.data;
                } else {
                    tireData = response.data;
                }
            }

            if (!tireData || !tireData.id) {
                throw new Error('No tire data received');
            }

            const formatDateForInput = (dateValue) => {
                if (!dateValue) return '';
                if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                    return dateValue;
                }
                if (typeof dateValue === 'string' && dateValue.includes('T')) {
                    return dateValue.split('T')[0];
                }
                try {
                    const date = new Date(dateValue);
                    if (!isNaN(date.getTime())) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    }
                } catch (e) {
                    console.warn('Failed to parse date:', dateValue, e);
                }
                return '';
            };

            // Set existing image if available
            if (tireData.image) {
                setExistingImage(tireData.image);
            } else {
                setExistingImage(null);
            }

            // Build distributions from inventories
            let totalQty = tireData.quantity || 0;
            const distRows = [];

            if (tireData.inventories && Array.isArray(tireData.inventories) && tireData.inventories.length > 0) {
                totalQty = tireData.inventories.reduce((sum, inv) => sum + (parseInt(inv.quantity) || 0), 0);
                for (const inv of tireData.inventories) {
                    const locId = getIdFromField(inv.location_id) || getIdFromField(inv.location);
                    const zoneId = getIdFromField(inv.zone_id) || getIdFromField(inv.zone);
                    distRows.push({
                        location_id: locId,
                        zone_id: zoneId,
                        quantity: String(inv.quantity || 0),
                    });
                    if (locId) {
                        try {
                            const zoneResp = await zonesAPI.getByLocation(locId);
                            const zonesData = Array.isArray(zoneResp.data) ? zoneResp.data : extractData(zoneResp);
                            setZonesCache(prev => ({ ...prev, [locId]: zonesData }));
                        } catch (e) {
                            setZonesCache(prev => ({ ...prev, [locId]: [] }));
                        }
                    }
                }
            }

            setTotalQuantity(String(totalQty));
            if (distRows.length > 0) {
                setDistributions(distRows);
            } else {
                setDistributions([{ location_id: '', zone_id: '', quantity: '' }]);
            }

            const processedData = {
                tire_brand_id: getIdFromField(tireData.tire_brand || tireData.tire_brand_id),
                pattern_id: getIdFromField(tireData.pattern || tireData.pattern_id),
                supplier_id: getIdFromField(tireData.supplier || tireData.supplier_id),
                tire_type_id: getIdFromField(tireData.tire_type || tireData.tire_type_id),
                made_in_id: getIdFromField(tireData.made_in || tireData.made_in_id),
                dot: tireData.dot || '',
                date_of_production: formatDateForInput(tireData.date_of_production),
                date_received: formatDateForInput(tireData.date_received),
                cost_price: tireData.cost_price?.toString() || '',
                selling_price: tireData.selling_price?.toString() || '',
                tax_rate: tireData.tax_rate?.toString() || '',
                is_tax_inclusive: !!tireData.is_tax_inclusive,
                notes: tireData.notes || '',
                size: tireData.size || '',
                construction_type: tireData.construction_type || 'radial',
                fuel_rating: tireData.fuel_rating || '',
                water_rating: tireData.water_rating || '',
                active: tireData.active === 1 || tireData.active === true || tireData.active === '1',
                run_flat: tireData.run_flat === 1 || tireData.run_flat === true || tireData.run_flat === '1',
                image: null,
            };

            if (processedData.date_of_production) {
                setDateOfProduction(processedData.date_of_production);
            }

            // Convert DOT (WWYY) to week input format (YYYY-Www)
            if (processedData.dot && processedData.dot.length === 4) {
                const week = processedData.dot.substring(0, 2);
                const yearShort = processedData.dot.substring(2, 4);
                const year = `20${yearShort}`;
                setDotWeekInput(`${year}-W${parseInt(week)}`);
            }

            setFormData(processedData);
            return processedData;
        } catch (error) {
            console.error('Error fetching tire data:', error);
            throw error;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Fetch patterns for selected brand
    const fetchPatterns = useCallback(async (brandId) => {
        try {
            if (!brandId) { setPatterns([]); return; }
            const cleanBrandId = String(brandId).trim();
            const response = await patternsAPI.getByBrand(cleanBrandId);
            let patternsData = [];
            if (Array.isArray(response.data)) {
                patternsData = response.data;
            } else if (response.data && typeof response.data === 'object') {
                if (Array.isArray(response.data.data)) patternsData = response.data.data;
                else patternsData = extractData(response);
            } else {
                patternsData = extractData(response);
            }
            setPatterns(patternsData);
        } catch (error) {
            setPatterns([]);
        }
    }, []);

    const fetchZonesForLocation = useCallback(async (locationId) => {
        if (!locationId) return;
        if (zonesCache[locationId]) return;
        try {
            const response = await zonesAPI.getByLocation(locationId);
            const zonesData = Array.isArray(response.data) ? response.data : extractData(response);
            setZonesCache(prev => ({ ...prev, [locationId]: zonesData }));
        } catch (error) {
            setZonesCache(prev => ({ ...prev, [locationId]: [] }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zonesCache]);

    // Initial data load
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [, tireFormData] = await Promise.all([
                    fetchDropdownData(),
                    fetchTireData()
                ]);
                if (tireFormData.tire_brand_id) {
                    await fetchPatterns(tireFormData.tire_brand_id);
                }
                setDataLoaded(true);
            } catch (error) {
                console.error('Error loading data:', error);
                setErrors({ submit: 'Failed to load tire data. Please try again.' });
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [fetchDropdownData, fetchTireData, fetchPatterns]);

    // Update patterns when brand changes
    useEffect(() => {
        if (dataLoaded && formData.tire_brand_id) {
            fetchPatterns(formData.tire_brand_id);
        } else if (dataLoaded && !formData.tire_brand_id) {
            setPatterns([]);
            setFormData(prev => ({ ...prev, pattern_id: '' }));
            setFilteredTireTypes(allTireTypes);
        }
    }, [dataLoaded, formData.tire_brand_id, fetchPatterns, allTireTypes]);

    // Filter tire types when pattern is selected
    useEffect(() => {
        if (dataLoaded && formData.pattern_id && patterns.length > 0) {
            const selectedPattern = patterns.find(p => String(p.id) === String(formData.pattern_id));
            if (selectedPattern && selectedPattern.tire_types && Array.isArray(selectedPattern.tire_types)) {
                const patternTireTypeIds = selectedPattern.tire_types.map(type => String(type.id));
                const filtered = allTireTypes.filter(type => patternTireTypeIds.includes(String(type.id)));
                setFilteredTireTypes(filtered);
                if (formData.tire_type_id && !filtered.find(t => String(t.id) === String(formData.tire_type_id))) {
                    setFormData(prev => ({ ...prev, tire_type_id: '' }));
                }
            } else {
                setFilteredTireTypes(allTireTypes);
            }
        } else if (dataLoaded) {
            setFilteredTireTypes(allTireTypes);
        }
    }, [dataLoaded, formData.pattern_id, formData.tire_type_id, patterns, allTireTypes]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = value;

        if (type === 'checkbox') {
            newValue = checked;
        }

        if (name === 'dot') {
            if (value && value.includes('-W')) {
                const [year, week] = value.split('-W');
                const yearShort = year.slice(-2);
                const weekPadded = week.padStart(2, '0');
                newValue = `${weekPadded}${yearShort}`;
                const weekNum = parseInt(week);
                const yearNum = parseInt(year);
                if (weekNum < 1 || weekNum > 53) {
                    setErrors(prev => ({ ...prev, dot: 'Week must be between 01 and 53' }));
                    setDateOfProduction('');
                } else {
                    setErrors(prev => ({ ...prev, dot: '' }));
                    setDateOfProduction(calculateDateFromWeek(yearNum, weekNum));
                }
            } else if (value) {
                const cleaned = value.replace(/\D/g, '').substring(0, 4);
                newValue = cleaned;
                if (cleaned.length === 4) {
                    const week = parseInt(cleaned.substring(0, 2));
                    const year = 2000 + parseInt(cleaned.substring(2, 4));
                    if (week < 1 || week > 53) {
                        setErrors(prev => ({ ...prev, dot: 'Week must be between 01 and 53' }));
                        setDateOfProduction('');
                    } else {
                        setErrors(prev => ({ ...prev, dot: '' }));
                        setDateOfProduction(calculateDateFromWeek(year, week));
                    }
                } else if (cleaned.length > 0) {
                    setErrors(prev => ({ ...prev, dot: 'DOT must be exactly 4 digits (WWYY)' }));
                } else {
                    setErrors(prev => ({ ...prev, dot: '' }));
                }
            } else {
                newValue = '';
                setDateOfProduction('');
                setErrors(prev => ({ ...prev, dot: '' }));
            }
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));
        if (errors[name] && name !== 'dot') {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleTireSizeKeyDown = (e) => {
        if (e.key === ' ') {
            e.preventDefault();
            const currentValue = e.target.value;
            if (!currentValue.includes('/')) {
                e.target.value = currentValue + ' / ';
            } else if (!currentValue.includes('R')) {
                e.target.value = currentValue + ' R ';
            }
            setFormData(prev => ({ ...prev, size: e.target.value }));
        }
    };

    // Build a set of used location/zone combination keys (excluding a given row index)
    const getUsedCombinations = (excludeIndex = -1) => {
        const used = new Set();
        distributions.forEach((d, i) => {
            if (i !== excludeIndex && d.location_id) {
                const key = `${d.location_id}-${d.zone_id || ''}`;
                used.add(key);
            }
        });
        return used;
    };

    // Check if a specific location/zone combo is already used in another row
    const isCombinationUsed = (locationId, zoneId, excludeIndex) => {
        const key = `${locationId}-${zoneId || ''}`;
        const used = getUsedCombinations(excludeIndex);
        return used.has(key);
    };

    // Distribution handlers
    const handleDistributionChange = (index, field, value) => {
        setInventoryChanged(true);
        const updated = [...distributions];
        updated[index] = { ...updated[index], [field]: value };

        if (field === 'location_id') {
            updated[index].zone_id = '';
            if (value) fetchZonesForLocation(value);
        }

        const newLocId = updated[index].location_id;
        const newZoneId = updated[index].zone_id;
        if (newLocId) {
            const dupKey = `dist_${index}_duplicate`;
            const isDup = isCombinationUsed(newLocId, newZoneId, index);
            if (isDup) {
                setErrors(prev => ({ ...prev, [dupKey]: 'This location/zone combination has already been added to the distribution list.' }));
            } else {
                setErrors(prev => {
                    const next = { ...prev };
                    delete next[dupKey];
                    distributions.forEach((_, otherIdx) => {
                        if (otherIdx !== index) {
                            const otherKey = `dist_${otherIdx}_duplicate`;
                            if (next[otherKey]) {
                                const otherLoc = updated[otherIdx]?.location_id;
                                const otherZone = updated[otherIdx]?.zone_id;
                                const otherCombo = `${otherLoc}-${otherZone || ''}`;
                                const count = updated.filter((d, j) => j !== otherIdx && d.location_id && `${d.location_id}-${d.zone_id || ''}` === otherCombo).length;
                                if (count === 0) delete next[otherKey];
                            }
                        }
                    });
                    return next;
                });
            }
        }

        setDistributions(updated);

        if (errors.distributions) {
            setErrors(prev => ({ ...prev, distributions: '' }));
        }
    };

    const addDistributionRow = () => {
        setInventoryChanged(true);
        setDistributions(prev => [...prev, { location_id: '', zone_id: '', quantity: '' }]);
    };

    const removeDistributionRow = (index) => {
        if (distributions.length <= 1) return;
        setInventoryChanged(true);
        const updated = distributions.filter((_, i) => i !== index);
        setDistributions(updated);

        setErrors(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(key => {
                if (key.startsWith(`dist_${index}_`)) delete next[key];
            });
            Object.keys(next).forEach(key => {
                const match = key.match(/^dist_(\d+)_duplicate$/);
                if (match) delete next[key];
            });
            return next;
        });
    };

    const getAllocatedQuantity = () => {
        return distributions.reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
    };

    const getRemainingQuantity = () => {
        const total = parseInt(totalQuantity) || 0;
        return total - getAllocatedQuantity();
    };

    const validateStep = (step) => {
        const newErrors = {};
        switch (step) {
            case 0: // Basic
                if (!formData.tire_brand_id) newErrors.tire_brand_id = 'Brand is required';
                if (!formData.tire_type_id) newErrors.tire_type_id = 'Tire type is required';
                if (!formData.size) newErrors.size = 'Size is required';
                if (!formData.dot) {
                    newErrors.dot = 'DOT is required';
                } else if (formData.dot.length !== 4) {
                    newErrors.dot = 'DOT must be exactly 4 digits (WWYY format)';
                } else {
                    const week = parseInt(formData.dot.substring(0, 2));
                    if (week < 1 || week > 53) newErrors.dot = 'Week must be between 01 and 53';
                }
                break;
            case 1: // Specifications
                if (!formData.supplier_id) newErrors.supplier_id = 'Supplier is required';
                if (!formData.made_in_id) newErrors.made_in_id = 'Origin is required';
                if (!formData.date_received) newErrors.date_received = 'Date received is required';
                break;
            case 2: // Pricing & Inventory
                if (!formData.cost_price) newErrors.cost_price = 'Cost price is required';
                if (!formData.selling_price) newErrors.selling_price = 'Selling price is required';
                if (!totalQuantity || parseInt(totalQuantity) < 1) {
                    newErrors.total_quantity = 'Total quantity must be at least 1';
                }
                const hasAnyDistribution = distributions.some(d => d.location_id || d.quantity);
                if (hasAnyDistribution) {
                    let hasDistError = false;
                    const seenCombos = new Set();
                    distributions.forEach((d, i) => {
                        if (d.location_id || d.quantity) {
                            if (!d.location_id) {
                                newErrors[`dist_${i}_location`] = 'Location is required';
                                hasDistError = true;
                            }
                            if (!d.quantity || parseInt(d.quantity) < 1) {
                                newErrors[`dist_${i}_quantity`] = 'Quantity must be at least 1';
                                hasDistError = true;
                            }
                            if (d.location_id) {
                                const comboKey = `${d.location_id}-${d.zone_id || ''}`;
                                if (seenCombos.has(comboKey)) {
                                    newErrors[`dist_${i}_duplicate`] = 'This location/zone combination has already been added to the distribution list.';
                                    hasDistError = true;
                                }
                                seenCombos.add(comboKey);
                            }
                        }
                    });
                    if (!hasDistError && totalQuantity && getRemainingQuantity() !== 0) {
                        newErrors.distributions = `Allocated quantity (${getAllocatedQuantity()}) must equal total quantity (${totalQuantity})`;
                    }
                }
                break;
            default:
                break;
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
        }
    };

    const handlePrevious = () => {
        setActiveStep(prev => Math.max(prev - 1, 0));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(activeStep)) return;

        setSaving(true);
        try {
            const submitData = new FormData();

            // Step 0: Basic
            submitData.append('tire_brand_id', formData.tire_brand_id);
            submitData.append('tire_type_id', formData.tire_type_id);
            submitData.append('size', formData.size);
            submitData.append('dot', formData.dot);
            if (formData.pattern_id) submitData.append('pattern_id', formData.pattern_id);
            if (formData.construction_type) submitData.append('construction_type', formData.construction_type);

            // Step 1: Specifications
            submitData.append('supplier_id', formData.supplier_id);
            submitData.append('made_in_id', formData.made_in_id);
            submitData.append('date_received', formData.date_received);
            submitData.append('run_flat', formData.run_flat ? '1' : '0');
            submitData.append('active', formData.active ? '1' : '0');
            if (formData.fuel_rating) submitData.append('fuel_rating', formData.fuel_rating);
            if (formData.water_rating) submitData.append('water_rating', formData.water_rating);
            if (formData.notes) submitData.append('notes', formData.notes);

            // Image handling: new image replaces existing
            if (newImage && newImage instanceof File) {
                submitData.append('image', newImage);
            } else if (formData.image && formData.image instanceof File) {
                submitData.append('image', formData.image);
            }

            // Step 2: Pricing & Distributions
            submitData.append('cost_price', formData.cost_price);
            submitData.append('selling_price', formData.selling_price);
            submitData.append('tax_rate', formData.tax_rate || '0');
            submitData.append('is_tax_inclusive', formData.is_tax_inclusive ? '1' : '0');
            submitData.append('total_quantity', totalQuantity);

            // Only send inventory data when the user actually changed it
            if (inventoryChanged) {
                submitData.append('_inventory_changed', '1');
                const filledDistributions = distributions.filter(d => d.location_id && d.quantity);
                if (filledDistributions.length > 0) {
                    const distPayload = filledDistributions.map(d => ({
                        location_id: d.location_id,
                        zone_id: d.zone_id || null,
                        quantity: parseInt(d.quantity),
                    }));
                    submitData.append('distributions', JSON.stringify(distPayload));
                }
            }

            await tiresAPI.update(id, submitData);

            navigate('/tires', {
                state: { message: 'Tire product updated successfully!' }
            });
        } catch (error) {
            if (error.response?.status === 422 && error.response?.data?.errors) {
                const validationErrors = error.response.data.errors;
                const newErrors = {};
                Object.keys(validationErrors).forEach(field => {
                    newErrors[field] = Array.isArray(validationErrors[field])
                        ? validationErrors[field][0]
                        : validationErrors[field];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    submit: error.response?.data?.message || 'Failed to update tire product. Please try again.'
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const calculateProfit = () => {
        const cost = parseFloat(formData.cost_price) || 0;
        const selling = parseFloat(formData.selling_price) || 0;
        const taxRate = parseFloat(formData.tax_rate) || 0;
        const profit = selling - cost;
        const margin = cost > 0 ? (profit / cost * 100).toFixed(2) : 0;
        const finalPrice = formData.is_tax_inclusive
            ? selling
            : selling * (1 + taxRate / 100);
        return { profit, margin, finalPrice };
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <BackButton to="/tires" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Edit Tire Product</h1>
                        <p className="text-sm text-gray-600 mt-1">Update tire information and inventory</p>
                    </div>
                </div>
            </div>

            {/* Progress Steps */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        return (
                            <div key={step.id} className="flex-1 relative">
                                <div className="flex items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                            activeStep >= index
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-200 text-gray-600'
                                        }`}
                                    >
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 ml-3">
                                        <p className={`text-sm font-medium ${
                                            activeStep >= index ? 'text-gray-900' : 'text-gray-500'
                                        }`}>
                                            {step.name}
                                        </p>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <ChevronRight className="w-5 h-5 text-gray-400" />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                    {errors.submit && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-5 h-5" />
                            <span className="whitespace-pre-line">{errors.submit}</span>
                        </div>
                    )}

                    {/* ============ Step 0: Basic Information ============ */}
                    {activeStep === 0 && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Size */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tire Size <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="size"
                                        value={formData.size}
                                        onChange={handleChange}
                                        onKeyDown={handleTireSizeKeyDown}
                                        placeholder="e.g. 225 / 50 R 16"
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.size ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Press space to add / and R automatically</p>
                                    {errors.size && <p className="mt-1 text-sm text-red-600">{errors.size}</p>}
                                </div>

                                {/* Brand */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tire Brand <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="tire_brand_id"
                                        value={normalizeId(formData.tire_brand_id)}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.tire_brand_id ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    >
                                        <option value="">Select Brand ({tireBrands.length} available)</option>
                                        {tireBrands.map(brand => (
                                            <option key={brand.id} value={normalizeId(brand.id)}>{brand.name}</option>
                                        ))}
                                    </select>
                                    {errors.tire_brand_id && <p className="mt-1 text-sm text-red-600">{errors.tire_brand_id}</p>}
                                </div>

                                {/* Pattern */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Pattern</label>
                                    <select
                                        name="pattern_id"
                                        value={normalizeId(formData.pattern_id)}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={!formData.tire_brand_id}
                                    >
                                        <option value="">
                                            {!formData.tire_brand_id ? 'Select brand first' : `Select Pattern (${patterns.length} available)`}
                                        </option>
                                        {patterns.map(pattern => (
                                            <option key={pattern.id} value={normalizeId(pattern.id)}>{pattern.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Tire Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tire Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="tire_type_id"
                                        value={normalizeId(formData.tire_type_id)}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.tire_type_id ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                        disabled={formData.pattern_id && filteredTireTypes.length === 0}
                                    >
                                        <option value="">
                                            {formData.pattern_id && filteredTireTypes.length === 0
                                                ? 'No tire types available for this pattern'
                                                : `Select Tire Type (${filteredTireTypes.length} available)`}
                                        </option>
                                        {filteredTireTypes.map(type => (
                                            <option key={type.id} value={normalizeId(type.id)}>{type.name}</option>
                                        ))}
                                    </select>
                                    {errors.tire_type_id && <p className="mt-1 text-sm text-red-600">{errors.tire_type_id}</p>}
                                </div>

                                {/* DOT */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        DOT Code (Week-Year) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="week"
                                        name="dot"
                                        value={dotWeekInput}
                                        onChange={(e) => {
                                            setDotWeekInput(e.target.value);
                                            handleChange(e);
                                        }}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.dot ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    />
                                    {formData.dot.length === 4 && !errors.dot && (
                                        <p className="mt-1 text-xs text-green-600">
                                            Week {formData.dot.substring(0, 2)} of 20{formData.dot.substring(2)}
                                        </p>
                                    )}
                                    {errors.dot && <p className="mt-1 text-sm text-red-600">{errors.dot}</p>}
                                    <p className="mt-1 text-xs text-gray-500">Select the production week and year</p>
                                </div>

                                {/* Date of Production (read-only) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Production</label>
                                    <input
                                        type="text"
                                        value={dateOfProduction || 'Auto-calculated from DOT'}
                                        disabled
                                        readOnly
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                    />
                                </div>

                                {/* Construction Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Construction Type</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="construction_type"
                                                value="radial"
                                                checked={formData.construction_type === 'radial'}
                                                onChange={handleChange}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">Radial</span>
                                        </label>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="construction_type"
                                                value="bias-ply"
                                                checked={formData.construction_type === 'bias-ply'}
                                                onChange={handleChange}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">Bias-ply</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ============ Step 1: Specifications ============ */}
                    {activeStep === 1 && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Supplier */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Supplier <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="supplier_id"
                                        value={normalizeId(formData.supplier_id)}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.supplier_id ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    >
                                        <option value="">Select Supplier ({suppliers.length} available)</option>
                                        {suppliers.map(supplier => (
                                            <option key={supplier.id} value={normalizeId(supplier.id)}>{supplier.name}</option>
                                        ))}
                                    </select>
                                    {errors.supplier_id && <p className="mt-1 text-sm text-red-600">{errors.supplier_id}</p>}
                                </div>

                                {/* Made In */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Made In <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="made_in_id"
                                        value={normalizeId(formData.made_in_id)}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.made_in_id ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    >
                                        <option value="">Select Origin ({madeIns.length} available)</option>
                                        {madeIns.map(madeIn => (
                                            <option key={madeIn.id} value={normalizeId(madeIn.id)}>{madeIn.name}</option>
                                        ))}
                                    </select>
                                    {errors.made_in_id && <p className="mt-1 text-sm text-red-600">{errors.made_in_id}</p>}
                                </div>

                                {/* Date Received */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date Received <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="date_received"
                                        value={formData.date_received}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.date_received ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    />
                                    {errors.date_received && <p className="mt-1 text-sm text-red-600">{errors.date_received}</p>}
                                </div>

                                {/* Active & Run Flat */}
                                <div className="flex items-center gap-8">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="active"
                                            checked={formData.active}
                                            onChange={handleChange}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm font-medium text-gray-700">Active Product</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="run_flat"
                                            checked={formData.run_flat}
                                            onChange={handleChange}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm font-medium text-gray-700">Run Flat</span>
                                    </label>
                                </div>
                            </div>

                            {/* Performance Ratings */}
                            <div className="bg-gray-50 rounded-lg p-6">
                                <h4 className="text-sm font-semibold text-gray-900 mb-4">Performance Ratings</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Efficiency Rating</label>
                                        <div className="flex gap-2">
                                            {['A', 'B', 'C', 'D', 'E'].map(rating => (
                                                <button
                                                    key={rating}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, fuel_rating: rating }))}
                                                    className={`w-12 h-12 rounded-lg font-semibold transition-all ${
                                                        formData.fuel_rating === rating
                                                            ? 'bg-green-600 text-white'
                                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {rating}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Wet Grip Rating</label>
                                        <div className="flex gap-2">
                                            {['A', 'B', 'C', 'D', 'E'].map(rating => (
                                                <button
                                                    key={rating}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, water_rating: rating }))}
                                                    className={`w-12 h-12 rounded-lg font-semibold transition-all ${
                                                        formData.water_rating === rating
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {rating}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Image */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                                {/* Existing Image Display */}
                                {existingImage && !newImage && !(formData.image instanceof File) && (
                                    <div className="mb-3 bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Current Image</h4>
                                        <div className="flex items-center gap-4">
                                            <img
                                                src={buildAssetUrl(existingImage)}
                                                alt="Current product"
                                                className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'block';
                                                }}
                                            />
                                            <div style={{ display: 'none' }} className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                                                Image not found
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">Current Image</p>
                                                <p className="text-xs text-gray-500 mt-1">Upload a new image below to replace this one</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/jpg,image/webp"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            if (file.size > 5120 * 1024) {
                                                alert('Image size must be less than 5MB');
                                                e.target.value = '';
                                                return;
                                            }
                                            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
                                            if (!validTypes.includes(file.type)) {
                                                alert('Invalid file type. Please upload a JPEG, PNG, or WEBP image.');
                                                e.target.value = '';
                                                return;
                                            }
                                            setNewImage(file);
                                            setFormData(prev => ({ ...prev, image: file }));
                                        }
                                    }}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="mt-1 text-xs text-gray-500">Accepted formats: JPG, PNG, WEBP. Max size: 5MB</p>
                                {(newImage || (formData.image && formData.image instanceof File)) && (
                                    <div className="mt-2 text-sm text-green-600 flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4" />
                                        Image selected: {(newImage || formData.image).name}
                                    </div>
                                )}
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Add any additional notes..."
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}

                    {/* ============ Step 2: Pricing & Inventory ============ */}
                    {activeStep === 2 && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Inventory</h3>

                            {/* Pricing */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Cost Price <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            name="cost_price"
                                            value={formData.cost_price}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.cost_price ? 'border-red-300' : 'border-gray-200'
                                            }`}
                                        />
                                    </div>
                                    {errors.cost_price && <p className="mt-1 text-sm text-red-600">{errors.cost_price}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Selling Price <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            name="selling_price"
                                            value={formData.selling_price}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.selling_price ? 'border-red-300' : 'border-gray-200'
                                            }`}
                                        />
                                    </div>
                                    {errors.selling_price && <p className="mt-1 text-sm text-red-600">{errors.selling_price}</p>}
                                </div>
                            </div>

                            {/* Tax Configuration */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tax Rate (%)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        name="tax_rate"
                                        value={formData.tax_rate}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.tax_rate ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    />
                                    {errors.tax_rate && <p className="mt-1 text-sm text-red-600">{errors.tax_rate}</p>}
                                </div>
                                <div className="flex items-center">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="is_tax_inclusive"
                                            checked={formData.is_tax_inclusive}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                is_tax_inclusive: e.target.checked
                                            }))}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        <span className="ml-3 text-sm font-medium text-gray-700">Tax Inclusive</span>
                                    </label>
                                    <p className="ml-2 text-xs text-gray-500">
                                        {formData.is_tax_inclusive
                                            ? 'Selling price already includes tax'
                                            : 'Tax will be added to selling price'}
                                    </p>
                                </div>
                            </div>

                            {formData.cost_price && formData.selling_price && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-blue-900">Profit Margin</p>
                                            <p className="text-2xl font-bold text-blue-900">
                                                ${calculateProfit().profit.toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm text-blue-900">Margin %</p>
                                            <p className="text-2xl font-bold text-blue-900">
                                                {calculateProfit().margin}%
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-blue-900">Final Price (incl. tax)</p>
                                            <p className="text-xs text-blue-700 mt-0.5">
                                                {formData.is_tax_inclusive
                                                    ? 'Tax included in selling price'
                                                    : `Selling Price + ${formData.tax_rate || 0}% tax`}
                                            </p>
                                            <p className="text-2xl font-bold text-green-700">
                                                ${calculateProfit().finalPrice.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Stock Distribution */}
                            <div className="border-t pt-6">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Stock Distribution
                                    </h4>
                                </div>
                                <p className="text-xs text-gray-500 mb-4">
                                    Optional: Specify where to store the stock. If left empty, all stock will be placed in the Unallocated zone for later distribution.
                                </p>

                                {/* Total Quantity */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Total Quantity <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={totalQuantity}
                                        onChange={(e) => {
                                            setTotalQuantity(e.target.value);
                                            setInventoryChanged(true);
                                            if (errors.total_quantity) setErrors(prev => ({ ...prev, total_quantity: '' }));
                                        }}
                                        placeholder="Enter total stock quantity"
                                        min="1"
                                        className={`w-full max-w-xs px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.total_quantity ? 'border-red-300' : 'border-gray-200'
                                        }`}
                                    />
                                    {errors.total_quantity && <p className="mt-1 text-sm text-red-600">{errors.total_quantity}</p>}
                                </div>

                                {/* Remaining Counter */}
                                {totalQuantity && parseInt(totalQuantity) > 0 && (
                                    <div className={`mb-4 p-3 rounded-lg border ${
                                        getRemainingQuantity() === 0
                                            ? 'bg-green-50 border-green-200 text-green-700'
                                            : getRemainingQuantity() < 0
                                                ? 'bg-red-50 border-red-200 text-red-700'
                                                : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                                    }`}>
                                        <div className="flex items-center justify-between text-sm font-medium">
                                            <span>Allocated: {getAllocatedQuantity()} / {totalQuantity}</span>
                                            <span>
                                                {getRemainingQuantity() === 0
                                                    ? 'Fully allocated'
                                                    : getRemainingQuantity() > 0
                                                        ? `${getRemainingQuantity()} remaining`
                                                        : `${Math.abs(getRemainingQuantity())} over-allocated`}
                                            </span>
                                        </div>
                                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full transition-all ${
                                                    getRemainingQuantity() === 0
                                                        ? 'bg-green-500'
                                                        : getRemainingQuantity() < 0
                                                            ? 'bg-red-500'
                                                            : 'bg-yellow-500'
                                                }`}
                                                style={{ width: `${Math.min(100, (getAllocatedQuantity() / parseInt(totalQuantity)) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {errors.distributions && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        {errors.distributions}
                                    </div>
                                )}

                                {/* Distribution Rows */}
                                <div className="space-y-3">
                                    {distributions.map((dist, index) => {
                                        const usedCombos = getUsedCombinations(index);
                                        const hasDuplicateError = !!errors[`dist_${index}_duplicate`];

                                        return (
                                        <div key={index} className={`flex items-start gap-3 p-4 rounded-lg border ${
                                            hasDuplicateError ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            <div className="flex-1">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Location
                                                </label>
                                                <select
                                                    value={normalizeId(dist.location_id)}
                                                    onChange={(e) => handleDistributionChange(index, 'location_id', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                        errors[`dist_${index}_location`] || hasDuplicateError ? 'border-red-300' : 'border-gray-200'
                                                    }`}
                                                >
                                                    <option value="">Select Location</option>
                                                    {locations.map(loc => {
                                                        const locationZones = zonesCache[String(loc.id)] || [];
                                                        const noZoneUsed = usedCombos.has(`${loc.id}-`);
                                                        const allZonesUsed = locationZones.length > 0
                                                            ? locationZones.every(z => usedCombos.has(`${loc.id}-${z.id}`))
                                                            : noZoneUsed;
                                                        return (
                                                            <option
                                                                key={loc.id}
                                                                value={normalizeId(loc.id)}
                                                                disabled={allZonesUsed && String(loc.id) !== String(dist.location_id)}
                                                            >
                                                                {loc.name}{allZonesUsed && String(loc.id) !== String(dist.location_id) ? ' (all zones used)' : ''}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {errors[`dist_${index}_location`] && (
                                                    <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_location`]}</p>
                                                )}
                                            </div>

                                            <div className="flex-1">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
                                                <select
                                                    value={normalizeId(dist.zone_id)}
                                                    onChange={(e) => handleDistributionChange(index, 'zone_id', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                        hasDuplicateError ? 'border-red-300' : 'border-gray-200'
                                                    }`}
                                                    disabled={!dist.location_id || !(zonesCache[dist.location_id]?.length > 0)}
                                                >
                                                    <option value="">
                                                        {!dist.location_id
                                                            ? 'Select location first'
                                                            : zonesCache[dist.location_id]?.length > 0
                                                                ? 'Select Zone (optional)'
                                                                : 'No zones available'}
                                                    </option>
                                                    {(zonesCache[dist.location_id] || []).map(zone => {
                                                        const zoneUsed = usedCombos.has(`${dist.location_id}-${zone.id}`);
                                                        return (
                                                            <option
                                                                key={zone.id}
                                                                value={normalizeId(zone.id)}
                                                                disabled={zoneUsed}
                                                            >
                                                                {zone.name}{zoneUsed ? ' (already used)' : ''}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {hasDuplicateError && (
                                                    <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_duplicate`]}</p>
                                                )}
                                            </div>

                                            <div className="w-32">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Qty
                                                </label>
                                                <input
                                                    type="number"
                                                    value={dist.quantity}
                                                    onChange={(e) => handleDistributionChange(index, 'quantity', e.target.value)}
                                                    placeholder="0"
                                                    min="1"
                                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                        errors[`dist_${index}_quantity`] ? 'border-red-300' : 'border-gray-200'
                                                    }`}
                                                />
                                                {errors[`dist_${index}_quantity`] && (
                                                    <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_quantity`]}</p>
                                                )}
                                            </div>

                                            {distributions.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeDistributionRow(index)}
                                                    className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>

                                <button
                                    type="button"
                                    onClick={addDistributionRow}
                                    className="mt-3 px-4 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Distribution Row
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ============ Step 3: Review & Submit ============ */}
                    {activeStep === 3 && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Review & Submit</h3>
                            <div className="bg-gray-50 rounded-lg p-6 space-y-6">
                                {/* Basic Information Review */}
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Size:</span>
                                            <span className="ml-2 font-medium">{formData.size || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Brand:</span>
                                            <span className="ml-2 font-medium">
                                                {tireBrands.find(b => String(b.id) === String(formData.tire_brand_id))?.name || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Pattern:</span>
                                            <span className="ml-2 font-medium">
                                                {patterns.find(p => String(p.id) === String(formData.pattern_id))?.name || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Type:</span>
                                            <span className="ml-2 font-medium">
                                                {filteredTireTypes.find(t => String(t.id) === String(formData.tire_type_id))?.name || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">DOT:</span>
                                            <span className="ml-2 font-medium">{formData.dot || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Construction:</span>
                                            <span className="ml-2 font-medium capitalize">{formData.construction_type}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Specifications Review */}
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Specifications</h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Supplier:</span>
                                            <span className="ml-2 font-medium">
                                                {suppliers.find(s => String(s.id) === String(formData.supplier_id))?.name || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Made In:</span>
                                            <span className="ml-2 font-medium">
                                                {madeIns.find(m => String(m.id) === String(formData.made_in_id))?.name || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Run Flat:</span>
                                            <span className="ml-2 font-medium">{formData.run_flat ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Status:</span>
                                            <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                                formData.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                                {formData.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        {formData.fuel_rating && (
                                            <div>
                                                <span className="text-gray-600">Fuel Rating:</span>
                                                <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                                    {formData.fuel_rating}
                                                </span>
                                            </div>
                                        )}
                                        {formData.water_rating && (
                                            <div>
                                                <span className="text-gray-600">Water Rating:</span>
                                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                    {formData.water_rating}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Pricing Review */}
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Pricing</h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Cost Price:</span>
                                            <span className="ml-2 font-medium">${formData.cost_price}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Selling Price:</span>
                                            <span className="ml-2 font-medium">${formData.selling_price}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Profit:</span>
                                            <span className="ml-2 font-medium text-green-600">
                                                ${calculateProfit().profit.toFixed(2)} ({calculateProfit().margin}%)
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Total Quantity:</span>
                                            <span className="ml-2 font-medium">{totalQuantity} units</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Distribution Review */}
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Stock Distribution</h4>
                                    {distributions.some(d => d.location_id && d.quantity) ? (
                                        <div className="space-y-2">
                                            {distributions.filter(d => d.location_id && d.quantity).map((dist, index) => {
                                                const loc = locations.find(l => String(l.id) === String(dist.location_id));
                                                const zone = (zonesCache[dist.location_id] || []).find(z => String(z.id) === String(dist.zone_id));
                                                return (
                                                    <div key={index} className="flex items-center justify-between text-sm bg-white p-3 rounded-lg border border-gray-200">
                                                        <div className="flex items-center gap-2">
                                                            <MapPin className="w-4 h-4 text-gray-400" />
                                                            <span className="font-medium">{loc?.name || 'Unknown'}</span>
                                                            {zone && (
                                                                <span className="text-gray-500">/ {zone.name}</span>
                                                            )}
                                                        </div>
                                                        <span className="font-medium">{dist.quantity} units</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            All {totalQuantity} units will be placed in the Unallocated zone for later distribution.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {formData.notes && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Notes</h4>
                                    <p className="text-sm text-gray-700">{formData.notes}</p>
                                </div>
                            )}

                            {/* Image preview in review */}
                            {(newImage || (formData.image && formData.image instanceof File)) && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">New Product Image</h4>
                                    <div className="flex items-center gap-4">
                                        <img
                                            src={previewUrl}
                                            alt="Product preview"
                                            className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">{(newImage || formData.image).name}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {((newImage || formData.image).size / 1024).toFixed(2)} KB
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {existingImage && !newImage && !(formData.image instanceof File) && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Current Product Image</h4>
                                    <div className="flex items-center gap-4">
                                        <img
                                            src={buildAssetUrl(existingImage)}
                                            alt="Current product"
                                            className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">Existing image (no changes)</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between items-center mt-8 pt-6 border-t">
                        <button
                            type="button"
                            onClick={handlePrevious}
                            className={`px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 ${
                                activeStep === 0 ? 'invisible' : ''
                            }`}
                        >
                            Previous
                        </button>

                        <div className="flex items-center gap-3">
                            {activeStep < steps.length - 1 ? (
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Update Tire Product
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditTire;
